import React, { useEffect, useState } from 'react';

/**
 * Annotation creation modal — replaces window.prompt for entering title + note.
 * Singleton: a global event-driven open/close so the toolbar can fire it without
 * lifting state up. Resolves a promise with the user's input or null on cancel.
 */
let resolverRef = null;

export function openAnnotationModal({ kind = 'pin', defaultTitle = '', defaultNote = '', defaultColor = '#dc2626' } = {}) {
  return new Promise((resolve) => {
    resolverRef = resolve;
    window.dispatchEvent(new CustomEvent('nexus-annotation-open', {
      detail: { kind, defaultTitle, defaultNote, defaultColor }
    }));
  });
}

const COLORS = ['#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed'];

export default function AnnotationModal() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('pin');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [color, setColor] = useState('#dc2626');
  const [shared, setShared] = useState(true);

  useEffect(() => {
    const onOpen = (e) => {
      setKind(e.detail?.kind || 'pin');
      setTitle(e.detail?.defaultTitle || '');
      setNote(e.detail?.defaultNote || '');
      setColor(e.detail?.defaultColor || '#dc2626');
      setShared(true);
      setOpen(true);
      // Focus title input on next tick
      setTimeout(() => document.getElementById('ann-title-input')?.focus(), 50);
    };
    window.addEventListener('nexus-annotation-open', onOpen);
    return () => window.removeEventListener('nexus-annotation-open', onOpen);
  }, []);

  const close = (result) => {
    setOpen(false);
    if (resolverRef) {
      resolverRef(result);
      resolverRef = null;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') close(null);
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      close({ title, note, color, shared });
    }
  };

  if (!open) return null;

  return (
    <div style={overlay} onClick={() => close(null)} onKeyDown={onKeyDown}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              {kind === 'pin' ? 'Add Pin' : 'Add Shape Annotation'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
              Leave a note for your team
            </div>
          </div>
          <button onClick={() => close(null)} style={closeBtn}>×</button>
        </div>

        <label style={label}>Title</label>
        <input
          id="ann-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={kind === 'pin' ? 'e.g. Priority repair site' : 'e.g. Review zone — high priority'}
          style={input}
          autoFocus
        />

        <label style={label}>Note</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add context for your colleagues — what should they know about this location?"
          rows={4}
          style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <label style={label}>Color</label>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 28, height: 28, borderRadius: 6, border: color === c ? '3px solid #0f172a' : '1px solid #e2e8f0',
                background: c, cursor: 'pointer', padding: 0
              }}
              title={c}
              aria-label={`color ${c}`}
            />
          ))}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 13, color: '#0f172a', cursor: 'pointer' }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          <span>Share with team — colleagues will see this annotation</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <button onClick={() => close(null)} style={btnSecondary}>Cancel</button>
          <button onClick={() => close({ title, note, color, shared })} style={btnPrimary} disabled={!title.trim()}>
            Save annotation
          </button>
        </div>

        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>
          {kind === 'pin' ? 'Pin will appear at your click location' : 'Shape will appear as a labeled overlay'} · Esc to cancel · ⌘↵ to save
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modal = { background: 'white', borderRadius: 10, boxShadow: '0 20px 50px rgba(0,0,0,0.25)', width: 'min(440px, 90vw)', padding: '20px 24px 16px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' };
const closeBtn = { background: 'transparent', border: 'none', fontSize: 26, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 0 };
const label = { display: 'block', fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginTop: 12, marginBottom: 4 };
const input = { width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: 14, border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none', color: '#0f172a' };
const btnPrimary = { background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' };
